import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "../../components/layout/PublicLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { MapPin, Briefcase, Building2, UserCog, Compass, Euro, CheckCircle2, ArrowRight, Star, Car } from "lucide-react";

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#101820] flex items-center gap-3 mb-4">
        <span className="w-1.5 h-6 bg-[#C79A3B] rounded-full" /> {title}
      </h2>
      {children}
    </div>
  );
}

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.get(`/jobs/${slug}`).then(({ data }) => setJob(data)).catch(() => setJob(false)); }, [slug]);

  const apply = () => {
    if (!user) navigate("/register", { state: { redirect: `/apply/${slug}` } });
    else if (user.role === "admin") navigate("/admin");
    else navigate(`/apply/${slug}`);
  };

  if (job === false) return <PublicLayout><div className="max-w-3xl mx-auto py-24 text-center">Role not found.</div></PublicLayout>;
  if (!job) return <PublicLayout><div className="py-24 text-center text-muted-foreground">Loading role…</div></PublicLayout>;

  const meta = [
    { icon: MapPin, label: "Location", value: job.location },
    { icon: Briefcase, label: "Employment Type", value: job.employment_type },
    { icon: Building2, label: "Department", value: job.department },
    { icon: UserCog, label: "Reporting To", value: job.reporting_to },
    { icon: Compass, label: "Territory", value: job.territory },
    { icon: Euro, label: "Package", value: job.salary_display },
  ];

  return (
    <PublicLayout>
      <section className="bg-[#101820] text-[#FAF6EE] relative overflow-hidden">
        <div className="absolute inset-0 kera-pattern opacity-30" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <span className="text-xs uppercase tracking-[0.25em] text-[#C79A3B] font-semibold">{job.department}</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3">{job.title}</h1>
          <p className="mt-2 text-[#C79A3B] font-medium">KERA, Ireland</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {meta.map((m) => (
              <div key={m.label} className="bg-[#1A242F] border border-[#C79A3B]/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#C79A3B] text-xs uppercase tracking-wider"><m.icon className="h-4 w-4" /> {m.label}</div>
                <div className="mt-1 text-sm text-[#FAF6EE]/90">{m.value}</div>
              </div>
            ))}
          </div>
          {job.driving_licence_required !== false ? (
          <div className="mt-6 flex items-start gap-3 bg-[#C76A32]/15 border border-[#C76A32]/40 rounded-lg p-4" data-testid="driving-licence-requirement">
            <Car className="h-5 w-5 text-[#E4C675] shrink-0 mt-0.5" />
            <div>
              <span className="uppercase text-[11px] tracking-[0.2em] text-[#E4C675] font-semibold">Essential Requirement</span>
              <p className="text-sm text-[#FAF6EE]/90 mt-0.5">A <strong>full clean Irish/EU driving licence</strong> and willingness to travel nationwide are required for this field-based role.</p>
            </div>
          </div>
          ) : (
          <div className="mt-6 flex items-start gap-3 bg-[#2E7D32]/15 border border-[#2E7D32]/40 rounded-lg p-4" data-testid="hybrid-no-car-note">
            <Car className="h-5 w-5 text-[#E4C675] shrink-0 mt-0.5" />
            <div>
              <span className="uppercase text-[11px] tracking-[0.2em] text-[#E4C675] font-semibold">Hybrid &amp; Student-Friendly</span>
              <p className="text-sm text-[#FAF6EE]/90 mt-0.5"><strong>No driving licence or car required.</strong> Much of the work is remote or office-based, and any field activity is planned around Dublin public transport.</p>
            </div>
          </div>
          )}
          <Button data-testid="apply-top-button" onClick={apply}
            className="mt-8 bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full px-7 h-12 shadow-lg shadow-[#C79A3B]/25">
            Apply for this Role <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Section title="About KERA">
          {job.about.split("\n\n").map((p, i) => <p key={i} className="text-[#101820]/80 leading-relaxed mb-3">{p}</p>)}
        </Section>
        <Section title="The Opportunity">
          {job.opportunity.split("\n\n").map((p, i) => <p key={i} className="text-[#101820]/80 leading-relaxed mb-3">{p}</p>)}
        </Section>
        <Section title="Key Responsibilities">
          <div className="space-y-6">
            {job.responsibilities.map((r) => (
              <div key={r.heading} className="bg-white rounded-xl border border-[#C79A3B]/25 p-5 shadow-sm">
                <h3 className="font-heading font-semibold text-[#101820] mb-3">{r.heading}</h3>
                <ul className="space-y-2">
                  {r.items.map((it, i) => (
                    <li key={i} className="flex gap-2 text-sm text-[#101820]/75"><CheckCircle2 className="h-4 w-4 text-[#C79A3B] shrink-0 mt-0.5" /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Candidate Profile">
          <ul className="grid sm:grid-cols-2 gap-2">
            {job.candidate_profile.map((c, i) => {
              const strong = c.toLowerCase().includes("driving licence");
              return (
                <li key={i} className={`flex gap-2 text-sm ${strong ? "sm:col-span-2 bg-[#C76A32]/10 border border-[#C76A32]/40 rounded-lg p-3 font-semibold text-[#101820]" : "text-[#101820]/75"}`}>
                  {strong ? <Car className="h-4 w-4 text-[#C76A32] shrink-0 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-[#C79A3B] shrink-0 mt-0.5" />} {c}{strong && " — essential"}
                </li>
              );
            })}
          </ul>
        </Section>
        <Section title="Highly Desirable">
          <div className="bg-[#C79A3B]/10 border border-[#C79A3B]/30 rounded-xl p-5">
            <ul className="space-y-2">
              {job.highly_desirable.map((c, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#101820]/80"><Star className="h-4 w-4 text-[#C76A32] shrink-0 mt-0.5" /> {c}</li>
              ))}
            </ul>
          </div>
        </Section>
        <Section title={job.success_title || "Success in the First 12 Months"}>
          <ul className="space-y-2">
            {job.success_first_12_months.map((c, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#101820]/75"><CheckCircle2 className="h-4 w-4 text-[#2E7D32] shrink-0 mt-0.5" /> {c}</li>
            ))}
          </ul>
        </Section>
        <Section title={job.remuneration_title || "Remuneration"}>
          <p className="text-lg font-heading font-bold text-[#101820] mb-3">{job.salary_display}</p>
          <ul className="grid sm:grid-cols-2 gap-2">
            {job.remuneration.map((c, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#101820]/75"><CheckCircle2 className="h-4 w-4 text-[#C79A3B] shrink-0 mt-0.5" /> {c}</li>
            ))}
          </ul>
        </Section>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-14 rounded-2xl bg-[#101820] text-[#FAF6EE] p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 kera-pattern opacity-30" />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">READY TO BUILD SOMETHING BIGGER?</h2>
            <p className="mt-4 text-[#FAF6EE]/75 max-w-2xl mx-auto">
              {job.cta_text || "If you understand the Irish ethnic food market, know how to sell and have the ambition to build something bigger, we want to hear from you."}
            </p>
            <Button data-testid="apply-bottom-button" onClick={apply}
              className="mt-7 bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full px-8 h-12 shadow-lg shadow-[#C79A3B]/25">
              Apply for this Role <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      </article>
    </PublicLayout>
  );
}
